import { useEffect, useState } from "react";
import api, { openPdf } from "../lib/api";
import { Tag } from "lucide-react";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

/**
 * Wiederverwendbarer Etiketten-Druck-Button (Runde 115).
 * Lädt Vorlagen aus /api/label-templates, öffnet Dropdown mit Vorlagenauswahl
 * und ruft POST /api/label-templates/{id}/render.pdf mit den übergebenen
 * Kontext-Daten auf (SKU, MHD, Charge, Bestellnr., etc.).
 *
 * Props:
 *   data     - Objekt mit Schlüsseln passend zu Feld-Typen (siehe Backend)
 *   label    - Button-Label (optional, default „Etikett")
 *   size     - "sm" (default) | "md"
 *   testid   - data-testid Suffix
 *   filter   - Funktion (tpl) => bool zur Vorfilterung
 */
export default function LabelPickerButton({
  data = {},
  label = "Etikett",
  size = "sm",
  testid = "label-picker",
  filter = null,
  category = null,          // Runde 116: filtert Vorlagen server-seitig
  bulkRecords = null,       // wenn gesetzt: Bulk-Render statt Einzeldruck
}) {
  const [templates, setTemplates] = useState(null);   // null=nicht geladen
  const [busy, setBusy] = useState(false);

  const loadOnce = async () => {
    if (templates !== null) return;
    try {
      const { data: rows } = await api.get("/label-templates");
      setTemplates(rows || []);
    } catch { setTemplates([]); }
  };

  const print = async (tpl) => {
    setBusy(true);
    try {
      if (bulkRecords && bulkRecords.length > 0) {
        // openPdf sendet body direkt an api.post — axios kümmert sich um JSON
        await openPdf(`/label-templates/${tpl.id}/bulk-render.pdf`, {
          method: "POST",
          body: { records: bulkRecords },
        });
        toast.success(`${bulkRecords.length} Etiketten aus „${tpl.name}" gedruckt`);
      } else {
        await openPdf(`/label-templates/${tpl.id}/render.pdf`, {
          method: "POST",
          body: { data },
        });
        toast.success(`Etikett „${tpl.name}" gedruckt`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Druck fehlgeschlagen");
    } finally { setBusy(false); }
  };

  const visible = (templates || []).filter((t) => {
    if (category && t.category && t.category !== category) return false;
    return filter ? filter(t) : true;
  });
  const btnCls = size === "md"
    ? "inline-flex items-center gap-1 px-3 h-9 rounded-full text-sm font-semibold bg-[var(--pop-cream)] hover:bg-[var(--pop-cream-dark)] border border-[var(--border)]"
    : "inline-flex items-center gap-1 text-xs font-semibold text-[var(--pop-black)] hover:underline";

  return (
    <DropdownMenu onOpenChange={(o) => { if (o) loadOnce(); }}>
      <DropdownMenuTrigger asChild>
        <button data-testid={testid} disabled={busy} className={btnCls} title="Etiketten-Vorlage wählen">
          <Tag size={size === "md" ? 14 : 13} /> {label}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64" data-testid={`${testid}-menu`}>
        <DropdownMenuLabel className="text-xs">Etiketten-Vorlage wählen</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {templates === null && <DropdownMenuItem disabled>Lade…</DropdownMenuItem>}
        {templates !== null && visible.length === 0 && (
          <DropdownMenuItem disabled className="text-xs text-[var(--text-muted)]">
            Keine Vorlagen — im Etiketten-Designer anlegen
          </DropdownMenuItem>
        )}
        {visible.map((t) => (
          <DropdownMenuItem key={t.id}
                            data-testid={`${testid}-tpl-${t.id}`}
                            onClick={() => print(t)}
                            className="cursor-pointer">
            <div className="flex-1">
              <div className="text-sm font-semibold">{t.name}</div>
              <div className="text-[10px] text-[var(--text-muted)]">
                {t.width_mm}×{t.height_mm} mm · {(t.fields || []).length} Felder
              </div>
            </div>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
